import { AsyncPipe, NgIf, NgStyle } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import {
  MatLegacyPaginator as MatPaginator,
  MatLegacyPaginatorModule as MatPaginatorModule,
} from '@angular/material/legacy-paginator';
import { MatLegacySnackBarModule, MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import {
  MatLegacyTableDataSource as MatTableDataSource,
  MatLegacyTableModule as MatTableModule,
} from '@angular/material/legacy-table';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import {
  CuttingFurnitureBaseDialogComponent,
  CuttingMatBaseFurniture,
  CuttingOrderFurniture,
  DirectivesModule,
  LegacyDialogsService,
  NgxLocalizedNumbers,
  NgxLocalizedNumbersService,
  RoundModeEnum,
} from 'cloud-shared-lib';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import {
  catchError,
  concatMap,
  debounceTime,
  filter,
  finalize,
  switchMap,
  takeUntil,
  tap,
  timeout,
} from 'rxjs/operators';

import { SelectionModel } from '@angular/cdk/collections';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { OrderDetailService } from '../order-detail.service';

@Component({
  selector: 'cutting-furniture-list',
  templateUrl: './furniture-list.component.html',
  styleUrls: ['./furniture-list.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgStyle,
    AsyncPipe,
    NgxLocalizedNumbers,
    MatIconModule,
    FormsModule,
    DirectivesModule,
    MatMenuModule,
    MatCheckboxModule,
    MatTableModule,
    MatFormFieldModule,
    MatPaginatorModule,
    MatInputModule,
    MatButtonModule,
    MatTooltipModule,
    MatLegacySnackBarModule,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class FurnitureListComponent implements OnDestroy {
  @Input() editableOrder = false;

  @ViewChild('paginator', { static: true }) paginator: MatPaginator;

  @Output() needSaveOrder: EventEmitter<void> = new EventEmitter();

  update$: BehaviorSubject<void> = new BehaviorSubject(void 0);
  formChange$: Subject<CuttingOrderFurniture> = new Subject();

  orderSaved$: Subject<boolean> = new Subject();

  furnitureBaseDialogRef: MatDialogRef<CuttingFurnitureBaseDialogComponent>;

  selectedFurnitures = new SelectionModel<number>(true, []);
  selectedAll = false;

  data$: Observable<CuttingOrderFurniture[]>;
  unsubscribe$: Subject<void> = new Subject();

  dataSource: MatTableDataSource<CuttingOrderFurniture> = new MatTableDataSource([]);
  displayedColumns = ['selected', 'texture', 'name', 'article', 'count', 'measure', 'price', 'cost', 'delete'];

  constructor(
    private orderDetailService: OrderDetailService,
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
    private dialogsService: LegacyDialogsService,
    private localizedNumbersService: NgxLocalizedNumbersService
  ) {
    this.data$ = this.update$.pipe(
      switchMap(() => this.orderDetailService.getOrderFurniture()),
      filter(d => !_.isNil(d)),
      tap(d => (this.dataSource.data = d))
    );

    this.formChange$
      .pipe(
        takeUntil(this.unsubscribe$),
        debounceTime(200),
        concatMap(f => this.orderDetailService.saveOrderFurniture(f))
      )
      .subscribe(() => {
        this.update$.next();
        this.orderDetailService.getOrder();
        this.orderDetailService.getOrderEstimate();
      });
  }

  setImagePlug(e: Event): void {
    (e.target as HTMLImageElement).src = '';
  }

  addFurniture(showPrice: boolean, showQuantityInStock: boolean): void {
    let add$ = of(false);

    if (!this.orderDetailService.order) {
      add$ = this.dialogsService.confirm($localize`Заказ будет сохранен. Продолжить?`).pipe(
        filter(res => res),
        tap(() => this.needSaveOrder.emit()),
        switchMap(() =>
          this.orderSaved$.pipe(
            timeout(15000),
            catchError(() => of(false))
          )
        ),
        filter(res => res)
      );
    }

    const open = (): void => {
      this.furnitureBaseDialogRef = this.dialog.open(CuttingFurnitureBaseDialogComponent, {
        panelClass: 'material-base-furniture-list-dialog',
        disableClose: false,
        width: '1150px',
      });

      this.furnitureBaseDialogRef.componentInstance.showMaterialsPrice = showPrice;
      this.furnitureBaseDialogRef.componentInstance.currencySymbol =
        this.localizedNumbersService.getCurrentLocaleDefinition().currency;
      this.furnitureBaseDialogRef.componentInstance.showQuantityInStock = showQuantityInStock;
    };

    add$
      .pipe(
        tap(() => open()),
        concatMap(() => this.furnitureBaseDialogRef.afterClosed()),
        finalize(() => (this.furnitureBaseDialogRef = null))
      )
      .subscribe((result: CuttingMatBaseFurniture[] | undefined) => {
        if (result && result.length > 0) {
          const furnitures: CuttingOrderFurniture[] = result.map(f => _.cloneDeep(f as CuttingOrderFurniture));
          furnitures.forEach(f => (f.count = f.roundMode === RoundModeEnum.ToNumber ? f.roundNumber : 1));

          this.orderDetailService
            .addOrderFurnitures(furnitures, this.orderDetailService.order ? this.orderDetailService.order.id : 0)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(
              () => {
                this.update$.next();
                this.orderDetailService.getOrder();
                this.orderDetailService.getOrderEstimate();
              },
              () => {
                this.snackBar.open($localize`Не удалось добавить фурнитуру`);
              }
            );
        }
        this.furnitureBaseDialogRef = null;
      });
  }

  selectAll(): void {
    this.selectedFurnitures.clear();
    this.selectedAll = !this.selectedAll;
    if (this.selectedAll && this.dataSource.data.length > 0) {
      this.selectedFurnitures.select(...this.dataSource.data.map(d => d.id));
    }
  }

  deleteFurniture(furniture: CuttingOrderFurniture): void {
    this.dialogsService
      .confirm($localize`Удалить фурнитуру "${furniture.name}"?`)
      .pipe(
        filter(res => res),
        switchMap(() => this.orderDetailService.deleteOrderFurniture(furniture.id)),
        tap(() => {
          this.update$.next();
          this.orderDetailService.getOrder();
          this.orderDetailService.getOrderEstimate();
        }),
        catchError(() => {
          this.snackBar.open($localize`Не удалось удалить фурнитуру`);
          return of(undefined);
        })
      )
      .subscribe();
  }

  deleteFurnitures(): void {
    if (this.selectedFurnitures.selected.length === 0) {
      this.snackBar.open($localize`Выберите фурнитуру для удаления`);
      return;
    }

    this.dialogsService
      .confirm($localize`Удалить выбранную фурнитуру?`)
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(res => res),
        concatMap(() => this.orderDetailService.deleteOrderFurnitures(this.selectedFurnitures.selected)),
        tap(() => {
          this.update$.next();

          this.orderDetailService.getOrder();
          this.orderDetailService.getOrderEstimate();

          this.selectedFurnitures.clear();
          this.selectedAll = false;

          this.snackBar.open($localize`Фурнитура удалена`);
        }),
        catchError(() => {
          this.snackBar.open($localize`Не удалось удалить фурнитуру`);
          return of(undefined);
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
